import { capitalize, numberToCurrency } from '@hc-frontend/utils'
import makeBase from 'core/services/email/sendEmailFromAgent/makeBase'
import { COMPARE_PLANS, DETAILS } from 'sites/routes'
import getURLProfile from 'utils/pivot/member/getURLProfile'
import getExtendDuration from 'utils/pivot/plan/getExtendDuration'
import getExtendedTagMessage from 'utils/pivot/plan/getExtendedTagMessage'
import getMaxPolicies from 'utils/pivot/plan/getMaxPolicies'
import isEpicPlan from 'utils/pivot/plan/isEpicPlan'
import isRXPlan from 'utils/pivot/plan/isRXPlan'
import store from 'utils/store/withMiddleware'
import getPremium from 'core/controllerPlans/getPremium'
import getBadgeDental from 'core/controllerPlans/getBadgeDental'
import detailsDentalFees from 'core/controllerPlans/detailsDentalFees'
import getLatitudeBenefits from 'core/controllerPlans/renderDetails/getLatitudeBenefits'
import moneyFormat from 'utils/format/money'
import {
  parseBenefits,
  getExternalPlanTotal
} from 'utils/plans/parseExternalBenefits'
import {
  DESCRIPTION_FLEX_BENEFITS,
  EXTERNAL_BROCHURE_FLEX_BENEFITS
} from 'core/constants/products'

const asMoneyWithoutDecimal = value => numberToCurrency(+value)
const getProperty = (plans, property) => plans.map(plan => plan[property])
const getCoreProperty = (plans, property, cb) =>
  plans.map(plan => cb(plan.coreBenefits[property]))

const getPlansDurations = plans =>
  plans.map(plan => {
    const extendedDuration = getExtendDuration(plan, plan.period)
    const { policies } = getMaxPolicies(plan, plan.period)
    return `${getExtendedTagMessage({
      period: +plan.period,
      currentPolicy: +plan.policies,
      maxPolicies: policies,
      extendedDuration
    })} days`
  })

const getPlanName = plans =>
  plans.map(plan => `${plan.planName}${isRXPlan(plan) ? ' | Rx' : ''}`)

const isMultiCarrier = plans => plans.some(p => p.multiCarrierPlan)

function makeComparisonPayload({
  valuesForm,
  agentInformation,
  plans,
  salesCenterAgent,
  placement,
  latitudeJson
}) {
  const data = makeBase({ valuesForm, agentInformation, salesCenterAgent })
  const state = store.getState()
  const isMC = isMultiCarrier(plans)

  const urlProfile =
    getURLProfile({
      addPlanId: false,
      placement,
      addQuoteKey: true
    }) + `&uid=${state.hcStorage.healthcareUserId}`

  const idOptions = plans.map((_, index) =>
    isMC ? `${index * 4 + 1} - ${(index + 1) * 4}` : '' + (index + 1)
  )

  data.template_name = isMC
    ? 'plan-comparison-multicarrier'
    : 'plan-comparison'
  data.message.global_merge_vars = data.message.global_merge_vars.concat([
    {
      name: 'attributes',
      content: [
        {
          className: 'even',
          items: ['Plan Name', ...getPlanName(plans)]
        },
        {
          className: 'odd',
          items: ['Coverage Duration', ...getPlansDurations(plans)]
        },
        {
          className: 'even',
          items: [
            'Deductible',
            ...getCoreProperty(plans, 'deductible', asMoneyWithoutDecimal)
          ]
        },
        {
          className: 'odd',
          items: [
            'Coinsurance',
            ...getCoreProperty(plans, 'userCoinsurance', val => `${val}%`)
          ]
        },
        {
          className: 'even',
          items: [
            'Max Out of Pocket',
            ...getCoreProperty(plans, 'outOfPocketMax', asMoneyWithoutDecimal)
          ]
        },
        {
          className: 'odd',
          items: [
            'Total Policy Coverage',
            ...getCoreProperty(plans, 'lifetimeMax', asMoneyWithoutDecimal)
          ]
        },
        {
          className: 'even',
          items: ['Network Type', ...getCoreProperty(plans, 'network', capitalize)]
        },
        {
          className: 'odd',
          items: [
            'Network Provider',
            ...getCoreProperty(plans, 'networkProvider', capitalize)
          ]
        },
        {
          className: 'even',
          items: ['Monthly Premium*', ...getProperty(plans, 'monthlyPremium')]
        }
      ]
    },
    {
      name: 'linkToQuote',
      content: `https://${
        window.location.hostname
      }${COMPARE_PLANS}?${urlProfile}&plans=${plans
        .map(plan => `${plan.planId}-${plan.period}-${plan.policies}`)
        .join(',')}`
    },
    {
      name: 'idOptions',
      content: idOptions
    },
    {
      name: 'linksBrochure',
      content: plans.map(plan => plan.brochure)
    },
    {
      name: 'linksView',
      content: plans.map(
        plan =>
          `https://${window.location.hostname}${DETAILS}?${urlProfile}&planid=${plan.planId}&period=${plan.period}&policies=${plan.policies}`
      )
    },
    {
      name: 'hasEpic',
      content: plans.some(isEpicPlan)
    },
    {
      name: 'hasCompanion',
      content: plans.some(plan => !isEpicPlan(plan))
    }
  ])

  if (isMC) {
    const stateAny = state as any
    const brochures = state.brochures || {}

    const dentalPlan = state.dentalPlanId
      ? state.plans[state.dentalPlanId]
      : undefined
    const visionPlan = state.visionPlanId
      ? state.plans[state.visionPlanId]
      : undefined
    const latitudePlan = state.latitudeId
      ? state.plans[state.latitudeId]
      : undefined

    const dentalCost =
      (dentalPlan ? getPremium(dentalPlan) : 0) +
      (visionPlan ? getPremium(visionPlan) : 0)
    const latitudeCost = latitudePlan ? getPremium(latitudePlan) : 0

    const selectedBenefitLevels =
      stateAny.selectedBenefitLevels ||
      state.hcStorage?.selectedBenefitLevels ||
      {}
    const externalAddonIds: number[] = state.planId
      ? stateAny.addonsExternalByPlan?.[state.planId] || []
      : []
    const flexCost = externalAddonIds.reduce((acc: number, externalId) => {
      const externalPlan = (stateAny.externalPlans || []).find(
        (p: any) => p.id === externalId
      )
      if (!externalPlan) return acc
      return (
        acc +
        getExternalPlanTotal(
          externalPlan,
          selectedBenefitLevels[externalId] || {}
        )
      )
    }, 0)
    const fee = state.prices?.subscriptionFeeNumber || 0

    data.message.global_merge_vars.push({
      name: 'plan',
      content: {
        link: `https://${window.location.hostname}${DETAILS}?${urlProfile}`,
        title: state.planName
      }
    })

    data.message.global_merge_vars.push({
      name: 'planSummaries',
      content: plans.map((plan, index) => ({
        title: plan.planName,
        period: idOptions[index],
        price: plan.monthlyPremium
      }))
    })

    data.message.global_merge_vars.push({
      name: 'totals',
      content: plans.map((plan, index) => {
        const planPremium = +String(plan.monthlyPremium).replace(/[^0-9.]/g, '')
        return {
          period: idOptions[index],
          value: moneyFormat(
            planPremium + dentalCost + latitudeCost + flexCost
          )
        }
      })
    })

    data.message.global_merge_vars.push({
      name: 'prices',
      content: {
        dental: moneyFormat(dentalCost),
        latitude: moneyFormat(latitudeCost),
        flexBenefits: moneyFormat(flexCost),
        fee: moneyFormat(fee)
      }
    })

    if (state.dentalPlanId && dentalPlan) {
      const badgeData = getBadgeDental(
        stateAny.addOnTitle || dentalPlan.planName,
        state.hcStorage.members
      )
      data.message.global_merge_vars.push({
        name: 'dental',
        content: {
          name: state.dentalVisionTitle,
          fees: detailsDentalFees(dentalPlan, visionPlan),
          brochure: brochures[dentalPlan.brochureId],
          badge: badgeData.message,
          details: [
            `<span>Preventive<br />& Diagnostic <b>${dentalPlan.coreBenefits.preventiveDiagnostic}</b></span>`,
            `<span>Basic Services<br /><b>${dentalPlan.coreBenefits.basicServices}</span>`,
            `<span>Major Services<br /><b>${dentalPlan.coreBenefits.majorServices}</b></span>`,
            `<span>Deductible<br /><b>${dentalPlan.coreBenefits.deductible}</b></span>`
          ]
        }
      })
    }

    if (state.latitudeId && latitudePlan && latitudeJson) {
      const response = getLatitudeBenefits(latitudeJson.membershipBenefits)
      data.message.global_merge_vars.push({
        name: 'latitude',
        content: {
          name: latitudePlan.planName,
          brochure: brochures[latitudePlan.brochureId],
          message: response.membersBenefits,
          disclaimer: response.disclaimer,
          details: [
            `<span>Accident<br /><b>${asMoneyWithoutDecimal(
              +latitudePlan.coreBenefits.accidentalDeath
            )}</b></span>`,
            `<span>Critical illness<br /><b>${asMoneyWithoutDecimal(
              +latitudePlan.coreBenefits.criticalIllness
            )}</b></span>`,
            `<span>Accidental death<br /><b>${asMoneyWithoutDecimal(
              +latitudePlan.coreBenefits.accidentalDeath
            )}</b></span>`,
            `<span>Hospital benefits<br /><b>${asMoneyWithoutDecimal(
              +latitudePlan.coreBenefits.hospitalBenefits
            )}/day</b></span>`
          ]
        }
      })
    }

    const flexAddonId = externalAddonIds[0]
    if (flexAddonId) {
      const externalPlan = (state.externalPlans || []).find(
        p => p.id === flexAddonId
      )
      if (externalPlan) {
        const benefits = parseBenefits(externalPlan.products)
        data.message.global_merge_vars.push({
          name: 'flexBenefits',
          content: {
            name: externalPlan.name,
            brochure: EXTERNAL_BROCHURE_FLEX_BENEFITS,
            message: DESCRIPTION_FLEX_BENEFITS,
            details: benefits.map(
              benefit =>
                `<span>${benefit?.label}<br /><b>${benefit?.value}</b></span>`
            )
          }
        })
      }
    }
  }

  return data
}

export default makeComparisonPayload
